import {
  clearFlashNotice,
  clearFlashAlert,
} from '../redux/features/ui/uiSlice'

// Clears the flash messages when the route changes, so a message doesn't follow
// the user to a page it has nothing to do with.
//
// The exception is a message set by the code that is doing the navigating,
// e.g. `dispatch(setFlashNotice('Unlock instructions sent'))` then
// `navigate('/app/sign_in')`. That message is for the next page, so it has to
// survive the navigation. The two happen in the same synchronous run, so a
// flash set in the current run (before the microtask below) is kept; anything
// older is cleared. This means a screen has to set its flash and navigate
// without an `await` in between (set the flash after the await, not before).
//
// Another exception is a redirect: a `replace` navigation (`<Navigate replace>`)
// isn't the user going anywhere, it is the app sending them to the right page,
// so it keeps the flash. This matters for a flash that came from the server
// (see packs/Index.jsx): it is set at load, and the app may then redirect (say,
// a signed-in user's `/app/sign_in` to `/app/pages`) before they have seen it.
//
// The subscription is to the shared `history`, which calls back at the moment
// of the navigation (before React renders), and it covers the back button and
// `navigateTo()` as well as `useNavigate()`.
export function clearFlashOnNavigation(store, history) {
  const flashOf = () => store.getState().ui.flash
  let flash = flashOf()
  let setThisRun = false

  store.subscribe(() => {
    const next = flashOf()
    const newNotice = next.notice && next.notice !== flash.notice
    const newAlert = next.alert && next.alert !== flash.alert
    flash = next

    if (newNotice || newAlert) {
      setThisRun = true
      queueMicrotask(() => { setThisRun = false })
    }
  })

  return history.listen(({ action }) => {
    if (setThisRun || action === 'REPLACE') {
      return
    }

    const { notice, alert } = flashOf()
    if (notice) {
      store.dispatch(clearFlashNotice())
    }
    if (alert) {
      store.dispatch(clearFlashAlert())
    }
  })
}
